// hooks/api/softProgram.queries.ts
import { notifyError, notifySuccess } from "@/lib/notification";
import * as softProgramService from "@/services/softProgram.service";
import {
  SoftProgramCreateType,
  SoftProgramParamsType,
  SoftProgramType,
} from "@/services/softProgram.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

export const softProgramsKey = {
  all: ["softPrograms"] as const,
  lists: (filters: SoftProgramParamsType) =>
    [...softProgramsKey.all, "lists", filters] as const,
  detail: (id: string) => [...softProgramsKey.all, "detail", id] as const,
};

export const useSoftProgramsQuery = (filters: SoftProgramParamsType) =>
  useQuery({
    queryKey: softProgramsKey.lists(filters),
    queryFn: () => softProgramService.fetchAllSoftPrograms(filters),
    placeholderData: (prev) => prev,
  });

export const useSoftProgramByIdQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: softProgramsKey.detail(id),
    queryFn: () => softProgramService.fetchSoftProgramById(id),
    enabled: enabled && !!id,
  });

export const useCreateSoftProgramMutation = (
  filters: SoftProgramParamsType
) => {
  const queryClient = useQueryClient();
  return useMutation<SoftProgramType, AxiosError, SoftProgramCreateType>({
    mutationFn: (data) => softProgramService.createSoftProgram(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: softProgramsKey.lists(filters),
      });
      notifySuccess("Program created successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to create program"),
  });
};

export const useUpdateSoftProgramMutation = (
  filters: SoftProgramParamsType
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ _id, data }: { _id: string; data: SoftProgramCreateType }) =>
      softProgramService.updateSoftProgram(_id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: softProgramsKey.lists(filters),
      });
      queryClient.invalidateQueries({
        queryKey: softProgramsKey.detail(variables._id),
      });
      notifySuccess("Program updated successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to update program"),
  });
};

export const useDeleteSoftProgramMutation = (
  filters: SoftProgramParamsType
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (_id: string) => softProgramService.deleteSoftProgram(_id),
    onSuccess: (_, _id) => {
      queryClient.invalidateQueries({
        queryKey: softProgramsKey.lists(filters),
      });
      queryClient.removeQueries({ queryKey: softProgramsKey.detail(_id) });
      notifySuccess("Program deleted successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to delete program"),
  });
};
